import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart3, 
  TrendingUp, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Heart, 
  MessageCircle, 
  Share2, 
  Eye,
  Users,
  RefreshCw,
  ExternalLink,
  AlertTriangle
} from 'lucide-react';
import { SiFacebook, SiInstagram } from 'react-icons/si';

interface AnalyticsData {
  totalPosts: number;
  publishedPosts: number;
  failedPosts: number;
  pendingPosts: number;
  totalEngagement: {
    likes: number;
    comments: number;
    shares: number;
    views: number;
    reach: number;
  };
  platformBreakdown: Record<string, {
    posts: number;
    published: number;
    failed: number;
    engagement: { likes: number; comments: number; shares: number; views: number; reach: number; };
  }>;
  recentPosts: Array<{
    id: string;
    platform: string;
    status: string;
    platformPostUrl: string | null;
    publishedAt: string | null;
    createdAt: string;
    engagement: any;
    errorMessage: string | null;
  }>;
}

interface SocialMediaAnalyticsProps {
  restaurantId: string;
}

export function SocialMediaAnalytics({ restaurantId }: SocialMediaAnalyticsProps) {
  const [timeRange, setTimeRange] = useState('30');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');

  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - parseInt(timeRange));

  const queryParams = new URLSearchParams({
    restaurantId,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    ...(selectedPlatform !== 'all' && { platform: selectedPlatform }),
  });

  const { data: analytics, isLoading, refetch } = useQuery<AnalyticsData>({
    queryKey: ['/api/partner/social/analytics', restaurantId, timeRange, selectedPlatform],
    enabled: !!restaurantId,
    queryFn: async () => {
      const response = await fetch(`/api/partner/social/analytics?${queryParams}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch analytics');
      }
      return response.json();
    },
  });

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return <Badge variant="default" className="bg-green-500">Published</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'processing':
        return <Badge variant="outline">Processing</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'facebook':
        return <SiFacebook className="w-4 h-4 text-blue-600" />;
      case 'instagram':
        return <SiInstagram className="w-4 h-4 text-pink-600" />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Social Media Analytics</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-muted rounded w-24 mb-2" />
                <div className="h-8 bg-muted rounded w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Social Media Analytics</h2>
          <p className="text-sm text-muted-foreground">Track your post performance across platforms</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Platforms</SelectItem>
              <SelectItem value="facebook">Facebook</SelectItem>
              <SelectItem value="instagram">Instagram</SelectItem>
            </SelectContent>
          </Select>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Posts</p>
                <p className="text-2xl font-bold">{analytics?.totalPosts || 0}</p>
              </div>
              <div className="p-2 bg-primary/10 rounded-full">
                <BarChart3 className="w-5 h-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Published</p>
                <p className="text-2xl font-bold text-green-600">{analytics?.publishedPosts || 0}</p>
              </div>
              <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-full">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Failed</p>
                <p className="text-2xl font-bold text-red-600">{analytics?.failedPosts || 0}</p>
              </div>
              <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-full">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold text-amber-600">{analytics?.pendingPosts || 0}</p>
              </div>
              <div className="p-2 bg-amber-100 dark:bg-amber-900/20 rounded-full">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Total Engagement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <Heart className="w-5 h-5 mx-auto mb-1 text-red-500" />
                <p className="text-lg font-semibold">{formatNumber(analytics?.totalEngagement?.likes || 0)}</p>
                <p className="text-xs text-muted-foreground">Likes</p>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <MessageCircle className="w-5 h-5 mx-auto mb-1 text-blue-500" />
                <p className="text-lg font-semibold">{formatNumber(analytics?.totalEngagement?.comments || 0)}</p>
                <p className="text-xs text-muted-foreground">Comments</p>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <Share2 className="w-5 h-5 mx-auto mb-1 text-green-500" />
                <p className="text-lg font-semibold">{formatNumber(analytics?.totalEngagement?.shares || 0)}</p>
                <p className="text-xs text-muted-foreground">Shares</p>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <Eye className="w-5 h-5 mx-auto mb-1 text-purple-500" />
                <p className="text-lg font-semibold">{formatNumber(analytics?.totalEngagement?.views || 0)}</p>
                <p className="text-xs text-muted-foreground">Views</p>
              </div>
              <div className="text-center p-3 bg-muted/50 rounded-lg">
                <Users className="w-5 h-5 mx-auto mb-1 text-teal-500" />
                <p className="text-lg font-semibold">{formatNumber(analytics?.totalEngagement?.reach || 0)}</p>
                <p className="text-xs text-muted-foreground">Reach</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Platform Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.platformBreakdown && Object.keys(analytics.platformBreakdown).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(analytics.platformBreakdown).map(([platform, data]) => (
                  <div key={platform} className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2 min-w-[100px]">
                      {getPlatformIcon(platform)}
                      <span className="font-medium capitalize">{platform}</span>
                    </div>
                    <div className="flex-1 grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Posts: </span>
                        <span className="font-medium">{data.posts}</span>
                      </div>
                      <div>
                        <span className="text-green-600 font-medium">{data.published}</span>
                        <span className="text-muted-foreground"> published</span>
                      </div>
                      <div>
                        <span className="text-red-600 font-medium">{data.failed}</span>
                        <span className="text-muted-foreground"> failed</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No platform data available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Posts</CardTitle>
        </CardHeader>
        <CardContent>
          {analytics?.recentPosts && analytics.recentPosts.length > 0 ? (
            <div className="space-y-3">
              {analytics.recentPosts.map((post) => (
                <div key={post.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {getPlatformIcon(post.platform)}
                    <div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(post.status)}
                        {post.status === 'failed' && post.errorMessage && (
                          <span className="text-xs text-red-600 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {post.errorMessage.slice(0, 50)}...
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {post.publishedAt 
                          ? `Published: ${new Date(post.publishedAt).toLocaleString()}`
                          : `Created: ${new Date(post.createdAt).toLocaleString()}`
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {post.engagement && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Heart className="w-3 h-3" />
                          {post.engagement.likes || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" />
                          {post.engagement.comments || 0}
                        </span>
                      </div>
                    )}
                    {post.platformPostUrl && (
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => window.open(post.platformPostUrl!, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No posts found for this period</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
